// configureAmplify.ts — bootstrap Amplify v6 com Cognito User Pool + Hosted UI (OIDC via IdC).
//
// Chamado uma única vez em src/main.tsx ANTES de renderizar App.
// Usa as VITE_AMPLIFY_* vars (ver src/env.d.ts).
//
// Em dev local sem backend deployado, as vars podem vir vazias — neste caso
// o Amplify não inicializa Auth e o useSession retorna { user: null, status: 'unauthenticated' }.

import { Amplify } from 'aws-amplify';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';

// sec-frontend-token-hardening (HIGH-06): use sessionStorage instead of
// localStorage for Cognito tokens. Tokens lost on tab close; reduces XSS
// persistence window. Trade-off: UX = re-login per tab session.
class SessionStorageWrapper {
  setItem(key: string, value: string): Promise<void> {
    sessionStorage.setItem(key, value);
    return Promise.resolve();
  }
  getItem(key: string): Promise<string | null> {
    return Promise.resolve(sessionStorage.getItem(key));
  }
  removeItem(key: string): Promise<void> {
    sessionStorage.removeItem(key);
    return Promise.resolve();
  }
  clear(): Promise<void> {
    sessionStorage.clear();
    return Promise.resolve();
  }
}

let configured = false;

/**
 * Tenta configurar o Amplify usando variáveis de ambiente Vite.
 * Retorna `true` se configurou, `false` se as vars estão ausentes
 * (modo dev/preview sem backend).
 */
export function configureAmplify(): boolean {
  if (configured) return true;

  const userPoolId = import.meta.env.VITE_AMPLIFY_USER_POOL_ID;
  const userPoolClientId = import.meta.env.VITE_AMPLIFY_USER_POOL_CLIENT_ID;
  const region = import.meta.env.VITE_AMPLIFY_REGION;
  const identityPoolId = import.meta.env.VITE_AMPLIFY_IDENTITY_POOL_ID;
  const cognitoDomain = import.meta.env.VITE_AMPLIFY_COGNITO_DOMAIN;
  const appsyncEndpoint = import.meta.env.VITE_APPSYNC_ENDPOINT;
  const appsyncRegion = import.meta.env.VITE_APPSYNC_REGION;

  if (!userPoolId || !userPoolClientId || !region) {
    console.warn(
      '[Amplify] VITE_AMPLIFY_USER_POOL_ID/CLIENT_ID/REGION ausentes — Auth desabilitado.',
    );
    return false;
  }

  // Domain Cognito hosted UI: env explicit → fallback derivado (best-effort).
  // Em prod, sempre setar VITE_AMPLIFY_COGNITO_DOMAIN.
  const derivedDomainSuffix = userPoolId.split('_')[1];
  const domain =
    cognitoDomain ||
    (derivedDomainSuffix ? `${derivedDomainSuffix}.auth.${region}.amazoncognito.com` : null);

  // Amplify v6 aceita configurações distintas se identityPoolId está presente
  // (CognitoIdentityPoolConfig) ou não (CognitoUserPoolConfig). Optamos por
  // dois caminhos de chamada para evitar union/intersection conflito de tipos.
  const oauthSection = domain
    ? {
        loginWith: {
          oauth: {
            domain,
            scopes: ['openid', 'email', 'profile'] as ('openid' | 'email' | 'profile')[],
            redirectSignIn: [window.location.origin + '/auth/callback'],
            redirectSignOut: [window.location.origin],
            responseType: 'code' as const,
          },
        },
      }
    : {};

  if (identityPoolId) {
    Amplify.configure({
      Auth: {
        Cognito: { userPoolId, userPoolClientId, identityPoolId, ...oauthSection },
      },
      ...(appsyncEndpoint && appsyncRegion
        ? {
            API: {
              GraphQL: {
                endpoint: appsyncEndpoint,
                region: appsyncRegion,
                defaultAuthMode: 'userPool' as const,
              },
            },
          }
        : {}),
    });
  } else {
    Amplify.configure({
      Auth: {
        Cognito: { userPoolId, userPoolClientId, ...oauthSection },
      },
      ...(appsyncEndpoint && appsyncRegion
        ? {
            API: {
              GraphQL: {
                endpoint: appsyncEndpoint,
                region: appsyncRegion,
                defaultAuthMode: 'userPool' as const,
              },
            },
          }
        : {}),
    });
  }

  configured = true;
  // Activate session storage AFTER Amplify is configured (token provider
  // is initialized lazily).
  try {
    cognitoUserPoolsTokenProvider.setKeyValueStorage(new SessionStorageWrapper());
  } catch (err) {
    // If storage swap fails, log but don't break auth (Amplify continues w/ localStorage default).
    console.warn('[Amplify] sessionStorage token provider setup failed:', err);
  }
  return true;
}

export function isAmplifyConfigured(): boolean {
  return configured;
}
